/* ═══════════════════════════════════════════════════
   SCHEME DETAIL — Tabs, Accordions, Play Finder,
   Playcall Sheet & Favorites
   ═══════════════════════════════════════════════════ */

if (!customElements.get("scheme-detail-component")) {
  class SchemeDetailComponent extends HTMLElement {
    constructor() {
      super();
      this.data = null;
      this.schemeId = this.dataset.schemeId || "scheme";
      this.customerId = this.dataset.customerId || "";
      this.schemeName = this.dataset.schemeName || this.schemeId;
      this.schemeUrl = this.dataset.schemeUrl || "";
      this.activeLevel = "beginner";
      this.globalFavKey = this.customerId
        ? `filmstudy_allfavs_${this.customerId}`
        : `filmstudy_allfavs_guest`;
      this._updateFavKey();
      this.favorites = this._loadFavorites();
      this.finderFilter = "all";
      this.finderQuery = "";
      this._debounce = null;
    }

    connectedCallback() {
      /* Determine the level up front so intro/macros pick the right JSON on
         first paint (loadData confirms it again from the same URL param). */
      this.activeLevel = this._levelFromURL();
      this.bindTabs();
      this.renderIntro();
      this.renderMacros();
      this.loadData();
      this.bindLevelToggle();
      this.bindHeroImageLightbox();
      this.bindOptionImageLightbox();
    }

    /* beginner unless the URL explicitly asks for expert */
    _levelFromURL() {
      const level = new URLSearchParams(window.location.search).get("level");
      return level === "expert" ? "expert" : "beginner";
    }

    /* ══════════════════════════════════════════════
       DATA
       ══════════════════════════════════════════════ */

    loadData() {
      const params = new URLSearchParams(window.location.search);
      const level = params.get("level");

      /* Pick expert JSON if level=expert and the tag exists, otherwise default */
      let jsonTag = null;
      if (level === "expert") {
        jsonTag = this.querySelector("[data-sd-json-expert]") || this.querySelector("[data-sd-json]");
      } else {
        jsonTag = this.querySelector("[data-sd-json]");
      }

      if (!jsonTag) {
        this._hideLoading();
        return;
      }

      try {
        this.data = JSON.parse(jsonTag.textContent);
        this.activeLevel = level || "beginner";
        this._updateFavKey();
        this.favorites = this._loadFavorites();
        this._hideLoading();
        this._renderLevelBadge();
        this.renderStats();
        this.renderFormations();
        this.renderFinder();
        this.renderSheet();
        this.renderFavorites();
        this.updateFavCount();
        this._rebuildGlobalIndex();
      } catch (err) {
        console.error("[scheme-detail] Failed to parse JSON:", err);
        this._hideLoading();
      }
    }

    /* ══════════════════════════════════════════════
       INTRO TAB
       ══════════════════════════════════════════════ */

    renderIntro() {
      /* Expert level uses [data-sd-intro-expert] when present, else falls back
         to the default intro (same fallback logic as the plays JSON). */
      const jsonTag = this.activeLevel === "expert"
        ? this.querySelector("[data-sd-intro-expert]") || this.querySelector("[data-sd-intro]")
        : this.querySelector("[data-sd-intro]");
      if (!jsonTag) {
        const p = this.querySelector("[data-sd-intro-panel]");
        if (p) p.innerHTML = "";
        return;
      }

      let introData;
      try {
        introData = JSON.parse(jsonTag.textContent);
      } catch (err) {
        console.error("[scheme-detail] Failed to parse intro JSON:", err);
        return;
      }

      const panel = this.querySelector("[data-sd-intro-panel]");
      if (!panel) return;

      /* New format: tabs array (collapsibles, all open by default) */
      if (Array.isArray(introData.tabs) && introData.tabs.length > 0) {
        panel.innerHTML = this._renderIntroTabs(introData.tabs);
        this._bindIntroTabs(panel, introData.tabs);
        return;
      }

      /* Legacy format: single video + audibles slider */
      let html = "";

      if (introData.video_url) {
        html += `<div class="sd-intro-video-wrap">`;
        if (introData.video_title) {
          html += `<h3 class="sd-intro-video-title">${this._esc(introData.video_title)}</h3>`;
        }
        const introEmbedClass = this._isVimeo(introData.video_url) ? " sd-intro-video--embed" : "";
        html += `<div class="sd-intro-video${introEmbedClass}">
          ${this._videoInnerHTML(introData.video_url)}
        </div></div>`;
      }

      const audibles = introData.audibles || [];
      if (audibles.length > 0) {
        html += `
        <div class="sd-intro-audibles">
          <h3 class="sd-intro-audibles-title">Audibles</h3>
          ${this._renderIntroSlider(audibles, 0)}
        </div>`;
      }

      panel.innerHTML = html;
      if (audibles.length > 0) {
        this._bindIntroSlider(panel.querySelector(".sd-intro-audibles"), audibles.length);
      }
    }

    /* ══════════════════════════════════════════════
       MACROS TAB — macro formations (accordion) with
       scrollable options, styled like the Formations tab
       ══════════════════════════════════════════════ */

    renderMacros() {
      /* Expert level uses [data-sd-macros-expert] when present, else the default. */
      const jsonTag = this.activeLevel === "expert"
        ? this.querySelector("[data-sd-macros-expert]") || this.querySelector("[data-sd-macros]")
        : this.querySelector("[data-sd-macros]");
      if (!jsonTag) {
        const p = this.querySelector("[data-sd-macros-panel]");
        if (p) p.innerHTML = "";
        return;
      }

      let macrosData;
      try {
        macrosData = JSON.parse(jsonTag.textContent);
      } catch (err) {
        console.error("[scheme-detail] Failed to parse macros JSON:", err);
        return;
      }

      const panel = this.querySelector("[data-sd-macros-panel]");
      if (!panel) return;

      /* New format: macro_formations[] with plays[].
         Legacy: macro_formations[] with options[] (no plays) → one unnamed play.
         Legacy: tabs[] → one formation, one play, one option. */
      let formations = [];
      if (Array.isArray(macrosData.macro_formations)) {
        formations = macrosData.macro_formations.map((mf) => ({
          name: mf.name || "",
          plays: Array.isArray(mf.plays)
            ? mf.plays
            : (Array.isArray(mf.options) ? [{ name: "", options: mf.options }] : []),
        }));
      } else if (Array.isArray(macrosData.tabs)) {
        formations = macrosData.tabs.map((t) => ({
          name: t.title || "",
          plays: [{ name: "", options: [{ title: t.title || "", video: t.video, slider: t.slider }] }],
        }));
      }
      if (!formations.length) return;

      panel.innerHTML = formations.map((mf, fi) => this._renderMacroFormation(mf, fi)).join("");
      this._bindMacroFormations(panel, formations);
    }

    _renderMacroFormation(mf, fi) {
      const plays = Array.isArray(mf.plays) ? mf.plays : [];
      const open = fi === 0; /* first macro formation open by default */
      return `
        <div class="sd-formation sd-macro-formation${open ? " open" : ""}" data-macro-index="${fi}">
          <button class="sd-formation-header" aria-expanded="${open}">
            <span class="sd-formation-name">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              ${this._esc(mf.name)}
              <span class="sd-formation-count">${plays.length} play${plays.length !== 1 ? "s" : ""}</span>
            </span>
            <span class="sd-formation-header-right">
              <svg class="sd-formation-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </span>
          </button>
          <div class="sd-formation-body">
            ${plays.map((p, pi) => this._renderMacroPlay(p, fi, pi)).join("")}
          </div>
        </div>`;
    }

    _renderMacroPlay(play, fi, pi) {
      const options = Array.isArray(play.options) ? play.options : [];
      return `
        <div class="sd-play sd-macro-play" data-macro-index="${fi}" data-play-index="${pi}">
          <div class="sd-play-header" role="button" tabindex="0">
            <span class="sd-play-left">
              <span class="sd-play-name">${this._esc(play.name)}</span>
            </span>
            <span class="sd-play-right">
              <span class="sd-play-options-count">${options.length} option${options.length !== 1 ? "s" : ""}</span>
              <svg class="sd-play-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </span>
          </div>
          <div class="sd-play-body">
            ${this._renderOptionTabs(options)}
            ${options.map((o, oi) => this._renderMacroOption(o, oi, fi, pi)).join("")}
          </div>
        </div>`;
    }

    _renderMacroOption(o, oi, fi, pi) {
      const hasVideo = o.video && o.video.url;
      const images = (o.slider && Array.isArray(o.slider.images)) ? o.slider.images : [];
      const hasImage = !images.length && o.image && o.image.url; // single image — carousel overrides it
      const hasNotes = o.notes && o.notes.trim();
      const videoEmbedClass = hasVideo && this._isVimeo(o.video.url) ? " sd-option-media--embed" : "";
      const videoBlock = hasVideo
        ? `<div class="sd-option-media sd-option-media--video${videoEmbedClass}">${this._videoInnerHTML(o.video.url)}</div>`
        : "";
      const carouselBlock = images.length
        ? `<div class="sd-option-media sd-macro-carousel">${this._renderIntroSlider(images, (fi * 1000) + (pi * 100) + oi)}</div>`
        : "";
      const imageBlock = hasImage
        ? `<div class="sd-option-media sd-option-media--image"><img src="${this._esc(o.image.url)}" alt="${this._esc(o.image.alt || o.title || "")}" loading="lazy"></div>`
        : "";
      const staticBlock = carouselBlock || imageBlock; // carousel wins over the single image

      let mediaHTML;
      if (videoBlock && staticBlock) {
        mediaHTML = `<div class="sd-option-media-grid">${videoBlock}${staticBlock}</div>`;
      } else {
        // Notes-only options are valid — don't show the "No media" placeholder then.
        mediaHTML = videoBlock || staticBlock || (hasNotes ? "" : `<div class="sd-option-media"><div class="sd-option-media-placeholder">No media</div></div>`);
      }

      const notesHTML = hasNotes
        ? `<div class="sd-option-field sd-macro-notes">
             <div class="sd-option-field-label">Notes</div>
             <div class="sd-option-field-text">${this._esc(o.notes)}</div>
           </div>`
        : "";

      return `
        <div class="sd-option${oi === 0 ? " active" : ""}" data-option-index="${oi}">
          <div class="sd-option-title-row">
            <h4 class="sd-option-title">${this._esc(o.title || `Option ${oi + 1}`)}</h4>
          </div>
          ${notesHTML}
          ${mediaHTML}
        </div>`;
    }

    _bindMacroFormations(panel, formations) {
      /* Macro formation accordion toggle */
      panel.querySelectorAll(".sd-macro-formation > .sd-formation-header").forEach((btn) => {
        btn.addEventListener("click", () => {
          const f = btn.closest(".sd-macro-formation");
          const isOpen = f.classList.toggle("open");
          btn.setAttribute("aria-expanded", String(isOpen));
        });
      });

      /* Macro play accordion toggle */
      panel.querySelectorAll(".sd-macro-play > .sd-play-header").forEach((hdr) => {
        hdr.addEventListener("click", () => hdr.closest(".sd-macro-play").classList.toggle("open"));
      });

      /* Per play: option nav slider + image carousels */
      panel.querySelectorAll(".sd-macro-play").forEach((playEl) => {
        const fi = +playEl.dataset.macroIndex;
        const pi = +playEl.dataset.playIndex;
        const nav = playEl.querySelector("[data-sd-option-nav]");
        const optionPanels = playEl.querySelectorAll(".sd-option");
        if (nav) this._bindOptionNav(nav, optionPanels);

        const opts = (formations[fi] && formations[fi].plays && formations[fi].plays[pi] && formations[fi].plays[pi].options) || [];
        optionPanels.forEach((optEl, oi) => {
          const carousel = optEl.querySelector(".sd-macro-carousel");
          if (!carousel) return;
          const imgs = (opts[oi] && opts[oi].slider && opts[oi].slider.images) || [];
          if (imgs.length) this._bindIntroSlider(carousel, imgs.length);
        });
      });
    }

    _renderIntroTabs(tabs) {
      const chevron = `<svg class="sd-collapsible-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;
      return tabs.map((tab, tabIdx) => {
        const title = tab.title || "";
        let body = "";

        if (tab.description && tab.description.trim()) {
          body += `<div class="sd-intro-tab-desc">${this._esc(tab.description.trim())}</div>`;
        }

        if (tab.video && tab.video.url) {
          const embedClass = this._isVimeo(tab.video.url) ? " sd-intro-video--embed" : "";
          body += `<div class="sd-intro-video-wrap">`;
          if (tab.video.title) {
            body += `<h3 class="sd-intro-video-title">${this._esc(tab.video.title)}</h3>`;
          }
          body += `<div class="sd-intro-video${embedClass}">
            ${this._videoInnerHTML(tab.video.url)}
          </div></div>`;
        }

        const images = (tab.slider && Array.isArray(tab.slider.images)) ? tab.slider.images : [];
        if (images.length > 0) {
          body += `<div class="sd-intro-audibles">`;
          if (tab.slider.title) {
            body += `<h3 class="sd-intro-audibles-title">${this._esc(tab.slider.title)}</h3>`;
          }
          body += this._renderIntroSlider(images, tabIdx);
          body += `</div>`;
        }

        return `
          <div class="sd-intro-tab sd-collapsible open" data-intro-tab-index="${tabIdx}">
            <button class="sd-collapsible-header" type="button" aria-expanded="true">
              <span class="sd-intro-tab-title">${this._esc(title)}</span>
              ${chevron}
            </button>
            <div class="sd-collapsible-body">
              ${body}
            </div>
          </div>`;
      }).join("");
    }

    _renderIntroSlider(images, tabIdx) {
      const multi = images.length > 1;
      const arrows = multi ? `
          <button class="sd-intro-slider-arrow sd-intro-slider-arrow--prev" data-intro-prev aria-label="Previous">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </button>` : "";
      const arrowsNext = multi ? `
          <button class="sd-intro-slider-arrow sd-intro-slider-arrow--next" data-intro-next aria-label="Next">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </button>` : "";
      const dots = multi ? `
        <div class="sd-intro-slider-dots" data-intro-dots>
          ${images.map((_, i) => `<button class="sd-intro-dot${i === 0 ? " active" : ""}" data-dot-index="${i}" aria-label="Go to slide ${i + 1}"></button>`).join("")}
        </div>` : "";
      return `
        <div class="sd-intro-slider-wrap${multi ? "" : " sd-intro-slider-wrap--single"}">
          ${arrows}
          <div class="sd-intro-slider" data-intro-slider>
            <div class="sd-intro-slider-track" data-intro-track>
              ${images.map((img, i) => `
                <div class="sd-intro-slide" data-slide-index="${i}">
                  <img src="${this._esc(img.url)}" alt="${this._esc(img.alt || "")}" loading="lazy">
                  ${img.note && String(img.note).trim() ? `<div class="sd-slide-note">${this._esc(img.note)}</div>` : ""}
                </div>
              `).join("")}
            </div>
          </div>
          ${arrowsNext}
        </div>
        ${dots}`;
    }

    _bindIntroTabs(panel, tabs) {
      /* Collapsible toggle for each tab */
      panel.querySelectorAll(".sd-intro-tab > .sd-collapsible-header").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const wrap = btn.parentElement;
          const isOpen = wrap.classList.contains("open");
          wrap.classList.toggle("open", !isOpen);
          btn.setAttribute("aria-expanded", String(!isOpen));
        });
      });

      /* Bind slider inside each tab that has one */
      panel.querySelectorAll(".sd-intro-tab").forEach((tabEl, tabIdx) => {
        const audiblesWrap = tabEl.querySelector(".sd-intro-audibles");
        if (!audiblesWrap) return;
        const tab = tabs[tabIdx];
        const count = (tab && tab.slider && Array.isArray(tab.slider.images)) ? tab.slider.images.length : 0;
        if (count > 0) this._bindIntroSlider(audiblesWrap, count);
      });
    }

    _bindIntroSlider(scope, count) {
      /* Click-to-lightbox still works with 1 slide, so bind that first */
      scope.querySelectorAll(".sd-intro-slide img").forEach((img) => {
        img.style.cursor = "pointer";
        img.addEventListener("click", () => {
          this._openImageLightbox(img.currentSrc || img.src, img.alt);
        });
      });

      if (count < 2) return;

      const track = scope.querySelector("[data-intro-track]");
      const prevBtn = scope.querySelector("[data-intro-prev]");
      const nextBtn = scope.querySelector("[data-intro-next]");
      const dotsWrap = scope.querySelector("[data-intro-dots]");
      if (!track) return;

      let current = 0;

      const goTo = (idx) => {
        if (idx < 0) idx = count - 1;
        if (idx >= count) idx = 0;
        current = idx;
        track.style.transform = `translateX(-${current * 100}%)`;
        if (dotsWrap) {
          dotsWrap.querySelectorAll(".sd-intro-dot").forEach((d, i) => {
            d.classList.toggle("active", i === current);
          });
        }
      };

      if (prevBtn) prevBtn.addEventListener("click", () => goTo(current - 1));
      if (nextBtn) nextBtn.addEventListener("click", () => goTo(current + 1));

      if (dotsWrap) {
        dotsWrap.querySelectorAll(".sd-intro-dot").forEach((dot) => {
          dot.addEventListener("click", () => goTo(+dot.dataset.dotIndex));
        });
      }

      /* Touch/swipe support */
      let startX = 0;
      let diff = 0;
      const slider = scope.querySelector("[data-intro-slider]");
      if (slider) {
        slider.addEventListener("touchstart", (e) => { startX = e.touches[0].clientX; diff = 0; }, { passive: true });
        slider.addEventListener("touchmove", (e) => { diff = e.touches[0].clientX - startX; }, { passive: true });
        slider.addEventListener("touchend", () => {
          if (Math.abs(diff) > 50) {
            goTo(diff < 0 ? current + 1 : current - 1);
          }
        });
      }
    }

    _hideLoading() {
      const el = this.querySelector("[data-sd-loading]");
      if (el) el.style.display = "none";
    }

    _renderLevelBadge() {
      const badge = this.querySelector("[data-sd-level-badge]");
      if (!badge) return;
      const isExpert = this.activeLevel === "expert";
      badge.textContent = isExpert ? "Expert" : "Beginner";
      badge.classList.toggle("sd-hero-badge--expert", isExpert);
      badge.classList.toggle("sd-hero-badge--beginner", !isExpert);
      badge.style.display = "";
    }

    /* ══════════════════════════════════════════════
       LEVEL TOGGLE (Expert ↔ Beginner)
       ══════════════════════════════════════════════ */

    bindLevelToggle() {
      const toggle = this.querySelector("[data-sd-level-toggle]");
      if (!toggle) return;

      const btns = toggle.querySelectorAll(".sd-level-toggle-btn");

      /* Set initial active state from URL */
      btns.forEach((btn) => {
        btn.classList.toggle("active", btn.dataset.level === this.activeLevel);
      });

      btns.forEach((btn) => {
        btn.addEventListener("click", () => {
          const level = btn.dataset.level;
          if (level === this.activeLevel) return;

          /* Update active button */
          btns.forEach((b) => b.classList.remove("active"));
          btn.classList.add("active");

          /* Swap data source */
          let jsonTag = null;
          if (level === "expert") {
            jsonTag = this.querySelector("[data-sd-json-expert]") || this.querySelector("[data-sd-json]");
          } else {
            jsonTag = this.querySelector("[data-sd-json]");
          }

          if (!jsonTag) return;

          try {
            this.data = JSON.parse(jsonTag.textContent);
            this.activeLevel = level;
            this._updateFavKey();
            this.favorites = this._loadFavorites();

            /* Update URL without reload */
            const url = new URL(window.location);
            url.searchParams.set("level", level);
            history.replaceState(null, "", url);

            /* Re-render all sections */
            this._renderLevelBadge();
            this.renderIntro();
            this.renderMacros();
            this.renderStats();
            this.renderFormations();
            this.renderFinder();
            this.renderSheet();
            this.renderFavorites();
            this.updateFavCount();
            this._rebuildGlobalIndex();
          } catch (err) {
            console.error("[scheme-detail] Failed to parse JSON on level switch:", err);
          }
        });
      });
    }

    /* ══════════════════════════════════════════════
       IMAGE LIGHTBOX
       ══════════════════════════════════════════════ */

    _openImageLightbox(src, alt) {
      const overlay = document.createElement("div");
      overlay.className = "sd-lightbox-overlay";
      overlay.innerHTML = `
        <button class="sd-lightbox-close" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <img src="${src}" alt="${alt || ''}">
      `;

      document.body.appendChild(overlay);
      document.body.style.overflow = "hidden";

      requestAnimationFrame(() => overlay.classList.add("open"));

      const close = () => {
        overlay.classList.remove("open");
        document.body.style.overflow = "";
        setTimeout(() => overlay.remove(), 300);
      };

      overlay.querySelector(".sd-lightbox-close").addEventListener("click", (e) => {
        e.stopPropagation();
        close();
      });
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
      });

      const escHandler = (e) => {
        if (e.key === "Escape") {
          close();
          document.removeEventListener("keydown", escHandler);
        }
      };
      document.addEventListener("keydown", escHandler);
    }

    bindHeroImageLightbox() {
      const heroImg = this.querySelector(".sd-hero-img");
      if (!heroImg) return;

      heroImg.addEventListener("click", () => {
        const img = heroImg.querySelector("img");
        if (!img) return;
        this._openImageLightbox(img.currentSrc || img.src, img.alt);
      });
    }

    bindOptionImageLightbox() {
      this.addEventListener("click", (e) => {
        const mediaContainer = e.target.closest(".sd-option-media--image");
        if (!mediaContainer) return;
        /* Ignore clicks inside the modal (modal has its own context) */
        if (e.target.closest(".sd-modal-overlay")) return;
        const img = mediaContainer.querySelector("img");
        if (!img) return;
        this._openImageLightbox(img.currentSrc || img.src, img.alt);
      });
    }

    /* ══════════════════════════════════════════════
       TABS
       ══════════════════════════════════════════════ */

    bindTabs() {
      const tabs = this.querySelectorAll("[data-sd-tabs] .sd-tab");
      tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const target = tab.dataset.tab;

          tabs.forEach((t) => {
            t.classList.remove("active");
            t.setAttribute("aria-selected", "false");
          });
          tab.classList.add("active");
          tab.setAttribute("aria-selected", "true");

          this.querySelectorAll("[data-sd-panels] .sd-panel").forEach((p) => {
            p.classList.toggle("active", p.dataset.panel === target);
          });

          /* Re-render favorites when switching to that tab */
          if (target === "favorites") {
            this.renderFavorites();
          }
        });
      });
    }

    /* ══════════════════════════════════════════════
       STATS
       ══════════════════════════════════════════════ */

    renderStats() {
      if (!this.data) return;
      const el = this.querySelector("[data-sd-stats]");
      if (!el) return;

      const formations = this.data.formations || [];
      let playCount = 0;
      let optionCount = 0;
      formations.forEach((f) => {
        (f.plays || []).forEach((p) => {
          playCount++;
          optionCount += (p.options || []).length;
        });
      });

      el.innerHTML = `
        <div class="sd-stat">
          <span class="sd-stat-value">${formations.length}</span>
          <span class="sd-stat-label">Formations</span>
        </div>
        <div class="sd-stat">
          <span class="sd-stat-value">${playCount}</span>
          <span class="sd-stat-label">Plays</span>
        </div>
        <div class="sd-stat">
          <span class="sd-stat-value">${optionCount}</span>
          <span class="sd-stat-label">Options</span>
        </div>
      `;
    }

    /* ══════════════════════════════════════════════
       FORMATIONS TAB
       ══════════════════════════════════════════════ */

    renderFormations() {
      if (!this.data) return;
      const container = this.querySelector("[data-sd-formations]");
      if (!container) return;

      container.innerHTML = this.data.formations
        .map((formation, fi) => {
          const plays = formation.plays || [];
          const introList = Array.isArray(formation.intro)
            ? formation.intro
            : (formation.intro ? [formation.intro] : []);
          const hasIntroVideo = introList.some((i) => i && i.video && i.video.url);
          const introBadge = hasIntroVideo
            ? `<span class="sd-formation-intro-badge" aria-label="Has intro video">
                 <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M8 5v14l11-7z"/></svg>
                 Intro Video
               </span>`
            : "";
          return `
          <div class="sd-formation" data-formation-index="${fi}">
            <button class="sd-formation-header" aria-expanded="false">
              <span class="sd-formation-name">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/></svg>
                ${this._esc(formation.name)}
                <span class="sd-formation-count">${plays.length} play${plays.length !== 1 ? "s" : ""}</span>
              </span>
              <span class="sd-formation-header-right">
                ${introBadge}
                <svg class="sd-formation-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </span>
            </button>
            <div class="sd-formation-body">
              ${this._renderFormationSettings(formation.settings)}
              ${this._renderDefensiveSettings(formation.defensive_settings)}
              ${this._renderFormationIntro(formation.intro, plays.length > 0)}
              ${plays.map((play, pi) => this._renderPlay(play, fi, pi)).join("")}
            </div>
          </div>`;
        })
        .join("");

      this._bindAccordions(container);
    }

    _renderFormationSettings(settings) {
      if (!settings) return "";
      const text = typeof settings === "string"
        ? settings
        : Array.isArray(settings) ? settings.join("\n\n") : "";
      if (!text.trim()) return "";
      const chevron = `<svg class="sd-collapsible-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;
      return `
        <div class="sd-formation-settings sd-collapsible">
          <button class="sd-collapsible-header" type="button" aria-expanded="false">
            <span class="sd-option-field-label">Formation Settings</span>
            ${chevron}
          </button>
          <div class="sd-collapsible-body">
            <div class="sd-option-field-text">${this._esc(text)}</div>
          </div>
        </div>`;
    }

    _renderDefensiveSettings(ds) {
      if (!ds || !ds.image || !ds.image.url) return "";
      const chevron = `<svg class="sd-collapsible-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;
      return `
        <div class="sd-formation-defensive-settings sd-collapsible">
          <button class="sd-collapsible-header" type="button" aria-expanded="false">
            <span class="sd-option-field-label">Defensive Settings</span>
            ${chevron}
          </button>
          <div class="sd-collapsible-body">
            <div class="sd-option-media sd-option-media--image">
              <img src="${this._esc(ds.image.url)}" alt="${this._esc(ds.image.alt || "Defensive settings")}" loading="lazy">
            </div>
          </div>
        </div>`;
    }

    _renderFormationIntro(intro, collapsible = false) {
      if (!intro) return "";
      const list = Array.isArray(intro) ? intro : [intro];
      const valid = list.filter((i) => this._hasIntroContent(i));
      if (!valid.length) return "";

      const multiClass = valid.length > 1 ? " sd-formation-intro--multi" : "";

      let innerHTML = "";
      if (valid.length === 1) {
        innerHTML = this._renderIntroContent(valid[0]);
      } else {
        const firstTitle = this._esc(valid[0].title || "Intro 1");
        const navHTML = `
          <div class="sd-option-nav sd-formation-intro-nav" data-sd-intro-nav data-total="${valid.length}" data-current="0">
            <button class="sd-option-nav-arrow sd-option-nav-arrow--prev" data-sd-nav-prev aria-label="Previous intro">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            <span class="sd-option-nav-label" data-sd-nav-label><span class="sd-option-nav-name">${firstTitle}</span><span class="sd-option-nav-count">1 / ${valid.length}</span></span>
            <button class="sd-option-nav-arrow sd-option-nav-arrow--next" data-sd-nav-next aria-label="Next intro">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>`;
        const panelsHTML = valid
          .map(
            (iv, i) => `
            <div class="sd-formation-intro-panel${i === 0 ? " active" : ""}" data-sd-intro-panel="${i}">
              ${this._renderIntroContent(iv)}
            </div>`
          )
          .join("");
        innerHTML = `${navHTML}${panelsHTML}`;
      }

      if (collapsible) {
        const chevron = `<svg class="sd-collapsible-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;
        return `
          <div class="sd-formation-intro sd-collapsible${multiClass}">
            <button class="sd-collapsible-header" type="button" aria-expanded="false">
              <span class="sd-option-field-label">Intro</span>
              ${chevron}
            </button>
            <div class="sd-collapsible-body">
              ${innerHTML}
            </div>
          </div>`;
      }

      return `<div class="sd-formation-intro${multiClass}">${innerHTML}</div>`;
    }

    _hasIntroContent(intro) {
      if (!intro) return false;
      const hasTitle = intro.title && intro.title.trim();
      const notes = intro.notes || intro.text;
      const hasNotes = notes && notes.trim();
      const hasImage = intro.image && intro.image.url;
      const hasVideo = intro.video && intro.video.url;
      return !!(hasTitle || hasNotes || hasImage || hasVideo);
    }

    _renderIntroContent(intro) {
      const hasTitle = intro.title && intro.title.trim();
      const notes = intro.notes || intro.text;
      const hasNotes = notes && notes.trim();
      const hasImage = intro.image && intro.image.url;
      const hasVideo = intro.video && intro.video.url;

      let mediaHTML = "";
      if (hasImage || hasVideo) {
        const imageBlock = hasImage
          ? `<div class="sd-option-media sd-option-media--image"><img src="${this._esc(intro.image.url)}" alt="${this._esc(intro.image.alt || "")}" loading="lazy"></div>`
          : "";
        const videoEmbedClass = hasVideo && this._isVimeo(intro.video.url) ? " sd-option-media--embed" : "";
        const videoBlock = hasVideo
          ? `<div class="sd-option-media sd-option-media--video${videoEmbedClass}">${this._videoInnerHTML(intro.video.url)}</div>`
          : "";
        if (hasImage && hasVideo) {
          mediaHTML = `<div class="sd-option-media-grid">${imageBlock}${videoBlock}</div>`;
        } else {
          mediaHTML = imageBlock || videoBlock;
        }
      }

      const titleHTML = hasTitle
        ? `<h4 class="sd-formation-intro-title">${this._esc(intro.title)}</h4>`
        : "";
      const notesHTML = hasNotes
        ? `<div class="sd-option-field sd-formation-intro-notes">
             <div class="sd-option-field-label">Notes</div>
             <div class="sd-option-field-text">${this._esc(notes)}</div>
           </div>`
        : "";

      return `${titleHTML}${notesHTML}${mediaHTML}`;
    }

    _renderPlay(play, fi, pi) {
      const options = play.options || [];
      const isFav = this.favorites.includes(play.id);

      return `
      <div class="sd-play" data-play-id="${this._esc(play.id)}" data-formation-index="${fi}" data-play-index="${pi}">
        <div class="sd-play-header" role="button" tabindex="0">
          <span class="sd-play-left">
            <span class="sd-play-name">${this._esc(play.name)}</span>
          </span>
          <span class="sd-play-right">
            <span class="sd-play-options-count">${options.length} option${options.length !== 1 ? "s" : ""}</span>
            <button class="sd-fav-btn${isFav ? " favorited" : ""}" data-fav-id="${this._esc(play.id)}" title="Toggle favorite">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
            </button>
            <svg class="sd-play-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
          </span>
        </div>
        <div class="sd-play-body">
          ${this._renderPlaySettings(play.settings)}
          ${this._renderOptionTabs(options)}
          ${options.map((opt, oi) => this._renderOption(opt, oi, play.id)).join("")}
        </div>
      </div>`;
    }

    _renderPlaySettings(settings) {
      if (!settings) return "";
      const text = typeof settings === "string"
        ? settings
        : Array.isArray(settings) ? settings.join("\n\n") : "";
      if (!text.trim()) return "";
      return `
        <div class="sd-play-settings">
          <div class="sd-option-field-label">Defensive Settings</div>
          <div class="sd-option-field-text">${this._esc(text)}</div>
        </div>`;
    }

    _renderOptionSettings(settings) {
      if (!settings) return "";
      const text = typeof settings === "string"
        ? settings
        : Array.isArray(settings) ? settings.join("\n\n") : "";
      if (!text.trim()) return "";
      return `
        <div class="sd-option-settings">
          <div class="sd-option-field-label">Settings</div>
          <div class="sd-option-field-text">${this._esc(text)}</div>
        </div>`;
    }

    _renderOptionTabs(options) {
      if (options.length <= 1) return "";
      const firstTitle = this._esc(options[0].title || "Option 1");
      return `
      <div class="sd-option-nav" data-sd-option-nav data-total="${options.length}" data-current="0">
        <button class="sd-option-nav-arrow sd-option-nav-arrow--prev" data-sd-nav-prev aria-label="Previous option">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <span class="sd-option-nav-label" data-sd-nav-label><span class="sd-option-nav-name">${firstTitle}</span><span class="sd-option-nav-count">1 / ${options.length}</span></span>
        <button class="sd-option-nav-arrow sd-option-nav-arrow--next" data-sd-nav-next aria-label="Next option">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>`;
    }

    _optionFavId(playId, optionIndex) {
      return `${playId}::opt::${optionIndex}`;
    }

    _renderOption(opt, index, playId) {
      const sliderImgs = (opt.slider && Array.isArray(opt.slider.images)) ? opt.slider.images.filter((i) => i && i.url) : [];
      const hasSlider = sliderImgs.length > 0;
      const hasImage = !hasSlider && opt.image && opt.image.url;
      const hasVideo = opt.video && opt.video.url;
      let mediaHTML = "";

      if (hasSlider || hasImage || hasVideo) {
        const imageBlock = hasSlider
          ? `<div class="sd-option-media sd-option-media--slider">${this._renderIntroSlider(sliderImgs, index)}</div>`
          : hasImage
          ? `<div class="sd-option-media sd-option-media--image"><img src="${this._esc(opt.image.url)}" alt="${this._esc(opt.title || "")}" loading="lazy"></div>`
          : "";
        const videoEmbedClass = hasVideo && this._isVimeo(opt.video.url) ? " sd-option-media--embed" : "";
        const videoBlock = hasVideo
          ? `<div class="sd-option-media sd-option-media--video${videoEmbedClass}">${this._videoInnerHTML(opt.video.url)}</div>`
          : "";

        if ((hasSlider || hasImage) && hasVideo) {
          mediaHTML = `<div class="sd-option-media-grid">${imageBlock}${videoBlock}</div>`;
        } else {
          mediaHTML = imageBlock || videoBlock;
        }
      } else {
        mediaHTML = `<div class="sd-option-media"><div class="sd-option-media-placeholder">No media uploaded</div></div>`;
      }

      /* Read categories from the option's categories array */
      const optCats = opt.categories || [];
      const catBadgesHTML = optCats.length
        ? optCats.map((c) => `<span class="sd-play-category">${this._esc(c)}</span>`).join("")
        : "";

      const optFavId = this._optionFavId(playId, index);
      const isOptFav = this.favorites.includes(optFavId);

      return `
      <div class="sd-option${index === 0 ? " active" : ""}" data-option-index="${index}">
        <div class="sd-option-title-row">
          <h4 class="sd-option-title">${this._esc(opt.title || `Option ${index + 1}`)}</h4>
          <div class="sd-option-title-actions">
            ${catBadgesHTML ? `<div class="sd-option-categories">${catBadgesHTML}</div>` : ""}
            <button class="sd-fav-btn${isOptFav ? " favorited" : ""}" data-fav-id="${this._esc(optFavId)}" title="Toggle favorite">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
            </button>
          </div>
        </div>
        ${this._renderOptionSettings(opt.settings)}
        ${mediaHTML}
        <div class="sd-option-fields">
          <div class="sd-option-field">
            <div class="sd-option-field-label">Setup</div>
            <div class="sd-option-field-text">${this._esc(opt.setup || "—")}</div>
          </div>
          <div class="sd-option-field">
            <div class="sd-option-field-label">Reads</div>
            <div class="sd-option-field-text">${this._esc(opt.reads || "—")}</div>
          </div>
        </div>
      </div>`;
    }

    _bindAccordions(container) {
      /* Formation accordions */
      container.querySelectorAll(".sd-formation-header").forEach((btn) => {
        btn.addEventListener("click", () => {
          const formation = btn.closest(".sd-formation");
          const isOpen = formation.classList.contains("open");
          formation.classList.toggle("open", !isOpen);
          btn.setAttribute("aria-expanded", !isOpen);
        });
      });

      /* Play accordions */
      container.querySelectorAll(".sd-play-header").forEach((hdr) => {
        hdr.addEventListener("click", (e) => {
          /* Ignore clicks on the fav button */
          if (e.target.closest(".sd-fav-btn")) return;
          const play = hdr.closest(".sd-play");
          play.classList.toggle("open");
        });
      });

      /* Option nav (prev/next slider) per play */
      container.querySelectorAll(".sd-play").forEach((play) => {
        const nav = play.querySelector("[data-sd-option-nav]");
        if (!nav) return;
        const panels = play.querySelectorAll(".sd-option");
        this._bindOptionNav(nav, panels);
      });

      /* Image slider inside any option that has one (e.g. defense coverage shells) */
      container.querySelectorAll(".sd-option .sd-option-media--slider").forEach((wrap) => {
        const count = wrap.querySelectorAll(".sd-intro-slide").length;
        if (count > 0) this._bindIntroSlider(wrap, count);
      });

      /* Generic collapsible toggles (defensive settings, collapsible intro) */
      container.querySelectorAll(".sd-collapsible > .sd-collapsible-header").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const wrap = btn.parentElement;
          const isOpen = wrap.classList.contains("open");
          wrap.classList.toggle("open", !isOpen);
          btn.setAttribute("aria-expanded", String(!isOpen));
        });
      });

      /* Intro nav (prev/next slider) for formations with multiple intros */
      container.querySelectorAll(".sd-formation-intro--multi").forEach((wrap) => {
        const nav = wrap.querySelector("[data-sd-intro-nav]");
        if (!nav) return;
        const panels = wrap.querySelectorAll(".sd-formation-intro-panel");
        this._bindIntroNav(nav, panels);
      });

      /* Favorite buttons */
      container.querySelectorAll(".sd-fav-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          this._toggleFavorite(btn.dataset.favId, btn);
        });
      });
    }

    /* ══════════════════════════════════════════════
       PLAY FINDER TAB
       ══════════════════════════════════════════════ */

    renderFinder() {
      if (!this.data) return;
      this._buildFinderFilters();
      this._bindFinderSearch();
      this.applyFinderFilters();
    }

    _buildFinderFilters() {
      const el = this.querySelector("[data-sd-finder-filters]");
      if (!el) return;

      const categories = new Set();
      (this.data.formations || []).forEach((f) => {
        (f.plays || []).forEach((p) => {
          (p.options || []).forEach((opt) => {
            (opt.categories || []).forEach((cat) => categories.add(cat));
          });
        });
      });

      let html = `<button class="sd-finder-filter active" data-finder-cat="all">All</button>`;
      categories.forEach((cat) => {
        html += `<button class="sd-finder-filter" data-finder-cat="${this._esc(cat.toLowerCase())}">${this._esc(cat)}</button>`;
      });
      el.innerHTML = html;

      el.querySelectorAll(".sd-finder-filter").forEach((btn) => {
        btn.addEventListener("click", () => {
          el.querySelectorAll(".sd-finder-filter").forEach((b) => b.classList.remove("active"));
          btn.classList.add("active");
          this.finderFilter = btn.dataset.finderCat;
          this.applyFinderFilters();
        });
      });
    }

    _bindFinderSearch() {
      const input = this.querySelector("[data-sd-finder-search]");
      if (!input) return;
      input.addEventListener("input", () => {
        clearTimeout(this._debounce);
        this._debounce = setTimeout(() => {
          this.finderQuery = input.value.trim().toLowerCase();
          this.applyFinderFilters();
        }, 200);
      });
    }

    applyFinderFilters() {
      const resultsEl = this.querySelector("[data-sd-finder-results]");
      const emptyEl = this.querySelector("[data-sd-finder-empty]");
      if (!resultsEl || !this.data) return;

      const plays = this._flatPlays();
      const visible = plays.filter((p) => {
        /* Collect all categories from this play's options */
        const allCats = (p.options || []).flatMap((opt) => opt.categories || []);
        const matchCat =
          this.finderFilter === "all" ||
          allCats.some((c) => c.toLowerCase() === this.finderFilter);
        const matchSearch =
          !this.finderQuery ||
          p.name.toLowerCase().includes(this.finderQuery) ||
          p.formation.toLowerCase().includes(this.finderQuery) ||
          allCats.some((c) => c.toLowerCase().includes(this.finderQuery));
        return matchCat && matchSearch;
      });

      if (visible.length === 0) {
        resultsEl.innerHTML = "";
        if (emptyEl) emptyEl.style.display = "block";
        return;
      }
      if (emptyEl) emptyEl.style.display = "none";

      resultsEl.innerHTML = visible
        .map((p) => {
          const isFav = this.favorites.includes(p.id);
          /* Unique categories across all options */
          const allCats = [...new Set((p.options || []).flatMap((opt) => opt.categories || []))];
          const catBadges = allCats
            .map((c) => `<span class="sd-play-category">${this._esc(c)}</span>`)
            .join("");
          return `
          <div class="sd-finder-row" data-finder-play-id="${this._esc(p.id)}" data-formation-index="${p.fi}" data-play-index="${p.pi}">
            <span class="sd-finder-row-left">
              ${catBadges}
              <span class="sd-finder-row-name">${this._esc(p.name)}</span>
              <span class="sd-finder-row-formation">${this._esc(p.formation)}</span>
            </span>
            <span class="sd-finder-row-right">
              <button class="sd-fav-btn${isFav ? " favorited" : ""}" data-fav-id="${this._esc(p.id)}" title="Toggle favorite">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
              </button>
            </span>
          </div>`;
        })
        .join("");

      /* Bind fav buttons */
      resultsEl.querySelectorAll(".sd-fav-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          this._toggleFavorite(btn.dataset.favId, btn);
        });
      });

      /* Click row → jump to formations tab and open that play */
      resultsEl.querySelectorAll(".sd-finder-row").forEach((row) => {
        row.addEventListener("click", () => {
          this._jumpToPlay(row.dataset.formationIndex, row.dataset.playIndex);
        });
      });
    }

    /* ══════════════════════════════════════════════
       PLAYCALL SHEET TAB
       ══════════════════════════════════════════════ */

    renderSheet() {
      if (!this.data) return;
      const el = this.querySelector("[data-sd-sheet]");
      if (!el) return;

      /* Auto-build the sheet by scanning every option's categories array.
         If a manual playcall_sheet exists, use its category order;
         otherwise derive order from first appearance. */
      const flatOpts = this._flatOptions();
      const catMap = new Map(); /* category → [ { opt, fi, pi, oi, playName, formation } ] */

      /* If manual sheet exists, seed the map in that order */
      if (this.data.playcall_sheet) {
        Object.keys(this.data.playcall_sheet).forEach((cat) => {
          if (!catMap.has(cat)) catMap.set(cat, []);
        });
      }

      /* Populate from option categories */
      flatOpts.forEach((item) => {
        (item.categories || []).forEach((cat) => {
          if (!catMap.has(cat)) catMap.set(cat, []);
          catMap.get(cat).push(item);
        });
      });

      // General notes box at the top of the Playcall Sheet tab (from the plays
      // JSON `notes` field). Omitted entirely when there are no notes.
      const notesHTML = (this.data.notes && String(this.data.notes).trim())
        ? `<div class="sd-option-field sd-sheet-notes">
             <div class="sd-option-field-label">Notes</div>
             <div class="sd-option-field-text">${this._esc(this.data.notes)}</div>
           </div>`
        : "";

      el.innerHTML = notesHTML + Array.from(catMap.entries())
        .map(([category, items]) => {
          if (items.length === 0) return "";
          return `
          <div class="sd-sheet-group">
            <button class="sd-sheet-group-header">
              <span class="sd-sheet-group-name">
                ${this._esc(category)}
                <span class="sd-sheet-group-count">${items.length}</span>
              </span>
              <svg class="sd-sheet-group-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            <div class="sd-sheet-group-body">
              ${items
                .map(
                  (item) => `
                <div class="sd-sheet-play" data-formation-index="${item.fi}" data-play-index="${item.pi}" data-option-index="${item.oi}">
                  <span class="sd-sheet-play-name">${this._esc(item.title)}</span>
                  <span class="sd-sheet-play-formation">${this._esc(item.formation)}</span>
                </div>`
                )
                .join("")}
            </div>
          </div>`;
        })
        .join("");

      /* Bind group toggles */
      el.querySelectorAll(".sd-sheet-group-header").forEach((btn) => {
        btn.addEventListener("click", () => {
          btn.closest(".sd-sheet-group").classList.toggle("open");
        });
      });

      /* Click option → open modal with full option content */
      el.querySelectorAll(".sd-sheet-play[data-formation-index]").forEach((row) => {
        row.addEventListener("click", () => {
          this._openOptionModal(row.dataset.formationIndex, row.dataset.playIndex, row.dataset.optionIndex);
        });
      });
    }

    /* ══════════════════════════════════════════════
       FAVORITES TAB
       ══════════════════════════════════════════════ */

    renderFavorites() {
      if (!this.data) return;
      const container = this.querySelector("[data-sd-favorites]");
      const emptyEl = this.querySelector("[data-sd-favorites-empty]");
      if (!container) return;

      /* Separate play-level and option-level favorites */
      const favPlays = this._flatPlays().filter((p) => this.favorites.includes(p.id));
      const favOptions = this._flatOptions().filter((o) =>
        this.favorites.includes(this._optionFavId(o.playId, o.oi))
      );

      if (favPlays.length === 0 && favOptions.length === 0) {
        container.innerHTML = "";
        if (emptyEl) emptyEl.style.display = "block";
        return;
      }
      if (emptyEl) emptyEl.style.display = "none";

      let html = "";

      /* Play favorites */
      if (favPlays.length > 0) {
        html += `<div class="sd-fav-section-label">Plays</div>`;
        html += favPlays
          .map((p) => {
            const allCats = [...new Set((p.options || []).flatMap((opt) => opt.categories || []))];
            const catBadges = allCats
              .map((c) => `<span class="sd-play-category">${this._esc(c)}</span>`)
              .join("");
            return `
            <div class="sd-finder-row" data-formation-index="${p.fi}" data-play-index="${p.pi}">
              <span class="sd-finder-row-left">
                ${catBadges}
                <span class="sd-finder-row-name">${this._esc(p.name)}</span>
                <span class="sd-finder-row-formation">${this._esc(p.formation)}</span>
              </span>
              <span class="sd-finder-row-right">
                <button class="sd-fav-btn favorited" data-fav-id="${this._esc(p.id)}" title="Remove favorite">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
                </button>
              </span>
            </div>`;
          })
          .join("");
      }

      /* Option favorites */
      if (favOptions.length > 0) {
        html += `<div class="sd-fav-section-label">Options</div>`;
        html += favOptions
          .map((o) => {
            const optFavId = this._optionFavId(o.playId, o.oi);
            const optCats = o.categories || [];
            const catBadges = optCats
              .map((c) => `<span class="sd-play-category">${this._esc(c)}</span>`)
              .join("");
            return `
            <div class="sd-finder-row" data-formation-index="${o.fi}" data-play-index="${o.pi}" data-option-index="${o.oi}">
              <span class="sd-finder-row-left">
                ${catBadges}
                <span class="sd-finder-row-name">${this._esc(o.title)}</span>
                <span class="sd-finder-row-formation">${this._esc(o.formation)} · ${this._esc(o.playName)}</span>
              </span>
              <span class="sd-finder-row-right">
                <button class="sd-fav-btn favorited" data-fav-id="${this._esc(optFavId)}" title="Remove favorite">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
                </button>
              </span>
            </div>`;
          })
          .join("");
      }

      container.innerHTML = html;

      /* Bind fav remove */
      container.querySelectorAll(".sd-fav-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          this._toggleFavorite(btn.dataset.favId, btn);
          this.renderFavorites();
        });
      });

      /* Click row → open modal popup */
      container.querySelectorAll(".sd-finder-row").forEach((row) => {
        row.addEventListener("click", () => {
          const fi = row.dataset.formationIndex;
          const pi = row.dataset.playIndex;
          if (row.dataset.optionIndex != null) {
            this._openOptionModal(fi, pi, row.dataset.optionIndex);
          } else {
            this._openPlayModal(fi, pi);
          }
        });
      });
    }

    /* ══════════════════════════════════════════════
       FAVORITES — localStorage
       ══════════════════════════════════════════════ */

    _updateFavKey() {
      this.favKey = this.customerId
        ? `filmstudy_favs_${this.customerId}_${this.schemeId}_${this.activeLevel}`
        : `filmstudy_favs_guest_${this.schemeId}_${this.activeLevel}`;
    }

    _loadFavorites() {
      try {
        return JSON.parse(localStorage.getItem(this.favKey)) || [];
      } catch {
        return [];
      }
    }

    _saveFavorites() {
      try {
        localStorage.setItem(this.favKey, JSON.stringify(this.favorites));
      } catch {
        /* quota exceeded — silent fail */
      }
    }

    _toggleFavorite(playId, btn) {
      const idx = this.favorites.indexOf(playId);
      const adding = idx === -1;
      if (adding) {
        this.favorites.push(playId);
      } else {
        this.favorites.splice(idx, 1);
      }
      this._saveFavorites();
      this._syncGlobalIndex(playId, adding);
      this.updateFavCount();

      /* Sync all fav buttons with this ID */
      this.querySelectorAll(`.sd-fav-btn[data-fav-id="${CSS.escape(playId)}"]`).forEach((b) => {
        b.classList.toggle("favorited", this.favorites.includes(playId));
      });
    }

    /* ── Global favorites index (cross-scheme) ── */

    _loadGlobalIndex() {
      try {
        return JSON.parse(localStorage.getItem(this.globalFavKey)) || [];
      } catch {
        return [];
      }
    }

    _saveGlobalIndex(index) {
      try {
        localStorage.setItem(this.globalFavKey, JSON.stringify(index));
      } catch { /* quota exceeded */ }
    }

    _syncGlobalIndex(playId, adding) {
      if (!this.data) return;
      const index = this._loadGlobalIndex();
      const lvl = this.activeLevel;

      if (adding) {
        /* Build entry with metadata */
        const entry = this._buildGlobalEntry(playId);
        if (entry && !index.some((e) => e.id === playId && e.schemeId === this.schemeId && e.level === lvl)) {
          index.push(entry);
        }
      } else {
        /* Remove entry */
        const removeIdx = index.findIndex((e) => e.id === playId && e.schemeId === this.schemeId && e.level === lvl);
        if (removeIdx > -1) index.splice(removeIdx, 1);
      }

      this._saveGlobalIndex(index);
    }

    _buildGlobalEntry(playId) {
      /* Check if it's an option-level favorite (format: "playId::opt::index") */
      const optMatch = playId.match(/^(.+)::opt::(\d+)$/);

      if (optMatch) {
        const parentPlayId = optMatch[1];
        const oi = parseInt(optMatch[2]);
        const flatOpt = this._flatOptions().find((o) => o.playId === parentPlayId && o.oi === oi);
        if (!flatOpt) return null;
        return {
          id: playId,
          type: "option",
          level: this.activeLevel,
          schemeId: this.schemeId,
          schemeName: this.schemeName,
          schemeUrl: this.schemeUrl,
          playName: flatOpt.playName,
          formation: flatOpt.formation,
          title: flatOpt.title,
          categories: flatOpt.categories,
          image: flatOpt.image || null,
          slider: flatOpt.slider || null,
          video: flatOpt.video || null,
          setup: flatOpt.setup || "",
          reads: flatOpt.reads || "",
          fi: flatOpt.fi,
          pi: flatOpt.pi,
          oi: flatOpt.oi,
        };
      } else {
        const flatPlay = this._flatPlays().find((p) => p.id === playId);
        if (!flatPlay) return null;
        const allCats = [...new Set((flatPlay.options || []).flatMap((opt) => opt.categories || []))];
        /* Store all options content for modal display */
        const options = (flatPlay.options || []).map((opt) => ({
          title: opt.title || "",
          categories: opt.categories || [],
          image: opt.image || null,
          slider: opt.slider || null,
          video: opt.video || null,
          setup: opt.setup || "",
          reads: opt.reads || "",
        }));
        return {
          id: playId,
          type: "play",
          level: this.activeLevel,
          schemeId: this.schemeId,
          schemeName: this.schemeName,
          schemeUrl: this.schemeUrl,
          playName: flatPlay.name,
          formation: flatPlay.formation,
          categories: allCats,
          options: options,
          fi: flatPlay.fi,
          pi: flatPlay.pi,
        };
      }
    }

    _rebuildGlobalIndex() {
      /* On page load, ensure all current favorites for this scheme + level
         are represented in the global index with fresh metadata */
      const index = this._loadGlobalIndex();
      const lvl = this.activeLevel;
      /* Remove stale entries for this scheme + level (keep other levels intact) */
      const filtered = index.filter((e) => !(e.schemeId === this.schemeId && e.level === lvl));
      /* Re-add current favorites */
      this.favorites.forEach((playId) => {
        const entry = this._buildGlobalEntry(playId);
        if (entry) filtered.push(entry);
      });
      this._saveGlobalIndex(filtered);
    }

    updateFavCount() {
      const badge = this.querySelector("[data-sd-fav-count]");
      if (!badge) return;
      const count = this.favorites.length;
      badge.textContent = count;
      badge.style.display = count > 0 ? "" : "none";
    }

    /* ══════════════════════════════════════════════
       HELPERS
       ══════════════════════════════════════════════ */

    /* Flatten all plays with formation context */
    _flatPlays() {
      const list = [];
      (this.data.formations || []).forEach((f, fi) => {
        (f.plays || []).forEach((p, pi) => {
          list.push({
            ...p,
            formation: f.name,
            fi,
            pi,
          });
        });
      });
      return list;
    }

    /* Flatten all options with formation/play context */
    _flatOptions() {
      const list = [];
      (this.data.formations || []).forEach((f, fi) => {
        (f.plays || []).forEach((p, pi) => {
          (p.options || []).forEach((opt, oi) => {
            list.push({
              ...opt,
              title: opt.title || `Option ${oi + 1}`,
              categories: opt.categories || [],
              playName: p.name,
              playId: p.id,
              formation: f.name,
              fi,
              pi,
              oi,
            });
          });
        });
      });
      return list;
    }

    /* ══════════════════════════════════════════════
       PLAY MODAL — shows all options with tabs
       ══════════════════════════════════════════════ */

    _openPlayModal(fi, pi) {
      const formation = this.data.formations[fi];
      if (!formation) return;
      const play = (formation.plays || [])[pi];
      if (!play) return;
      const options = play.options || [];

      /* Remove existing modal */
      const existing = this.querySelector(".sd-modal-overlay");
      if (existing) existing.remove();

      /* Build option content */
      let bodyHTML = this._renderPlaySettings(play.settings);
      if (options.length > 1) {
        const firstTitle = this._esc(options[0].title || "Option 1");
        bodyHTML += `<div class="sd-option-nav" data-sd-option-nav data-total="${options.length}" data-current="0">
          <button class="sd-option-nav-arrow sd-option-nav-arrow--prev" data-sd-nav-prev aria-label="Previous option">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <span class="sd-option-nav-label" data-sd-nav-label><span class="sd-option-nav-name">${firstTitle}</span><span class="sd-option-nav-count">1 / ${options.length}</span></span>
          <button class="sd-option-nav-arrow sd-option-nav-arrow--next" data-sd-nav-next aria-label="Next option">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>`;
        bodyHTML += options.map((opt, i) =>
          `<div class="sd-modal-option-panel${i === 0 ? " active" : ""}" data-sd-opt-panel="${i}">
            ${this._buildModalOptionContent(opt, i)}
          </div>`
        ).join("");
      } else if (options.length === 1) {
        bodyHTML += this._buildModalOptionContent(options[0], 0);
      } else {
        bodyHTML += `<p style="opacity:0.4;text-align:center;padding:20px;">No options available for this play.</p>`;
      }

      /* Play-level fav button */
      const isPlayFav = this.favorites.includes(play.id);

      const overlay = document.createElement("div");
      overlay.className = "sd-modal-overlay";
      overlay.innerHTML = `
        <div class="sd-modal">
          <div class="sd-modal-header">
            <div class="sd-modal-header-left">
              <span class="sd-modal-formation">${this._esc(formation.name)}</span>
              <span class="sd-modal-play-name">${this._esc(play.name)}</span>
            </div>
            <div class="sd-modal-header-right">
              <button class="sd-fav-btn${isPlayFav ? " favorited" : ""}" data-fav-id="${this._esc(play.id)}" title="Toggle favorite">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
              </button>
              <button class="sd-modal-close" aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
          </div>
          <div class="sd-modal-body">
            ${bodyHTML}
          </div>
        </div>
      `;

      this.appendChild(overlay);
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => overlay.classList.add("open"));

      /* Close handlers */
      const close = () => {
        overlay.classList.remove("open");
        document.body.style.overflow = "";
        const vid = overlay.querySelector("video");
        if (vid) vid.pause();
        setTimeout(() => overlay.remove(), 250);
      };

      overlay.querySelector(".sd-modal-close").addEventListener("click", close);
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
      });

      /* Fav button */
      const modalFavBtn = overlay.querySelector(".sd-fav-btn");
      if (modalFavBtn) {
        modalFavBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this._toggleFavorite(modalFavBtn.dataset.favId, modalFavBtn);
        });
      }

      /* Option nav (prev/next slider) */
      const modalNav = overlay.querySelector("[data-sd-option-nav]");
      if (modalNav) {
        const panels = overlay.querySelectorAll(".sd-modal-option-panel");
        this._bindOptionNav(modalNav, panels);
      }

      /* Lightbox on images */
      overlay.querySelectorAll(".sd-option-media--image").forEach((media) => {
        media.addEventListener("click", (e) => {
          e.stopPropagation();
          const img = media.querySelector("img");
          if (img) this._openImageLightbox(img.currentSrc || img.src, img.alt);
        });
      });

      /* Escape key */
      const escHandler = (e) => {
        if (e.key === "Escape") {
          close();
          document.removeEventListener("keydown", escHandler);
        }
      };
      document.addEventListener("keydown", escHandler);
    }

    _buildModalOptionContent(opt, oi) {
      const hasImage = opt.image && opt.image.url;
      const hasVideo = opt.video && opt.video.url;
      let mediaHTML = "";

      if (hasImage || hasVideo) {
        const imageBlock = hasImage
          ? `<div class="sd-option-media sd-option-media--image"><img src="${this._esc(opt.image.url)}" alt="${this._esc(opt.title || "")}" loading="lazy"></div>`
          : "";
        const videoEmbedClass = hasVideo && this._isVimeo(opt.video.url) ? " sd-option-media--embed" : "";
        const videoBlock = hasVideo
          ? `<div class="sd-option-media sd-option-media--video${videoEmbedClass}">${this._videoInnerHTML(opt.video.url)}</div>`
          : "";
        if (hasImage && hasVideo) {
          mediaHTML = `<div class="sd-option-media-grid">${imageBlock}${videoBlock}</div>`;
        } else {
          mediaHTML = imageBlock || videoBlock;
        }
      }

      const optCats = opt.categories || [];
      const catBadgesHTML = optCats.length
        ? `<div class="sd-option-categories">${optCats.map((c) => `<span class="sd-play-category">${this._esc(c)}</span>`).join("")}</div>`
        : "";

      return `
        <div class="sd-option-title-row">
          <h4 class="sd-option-title">${this._esc(opt.title || `Option ${+oi + 1}`)}</h4>
          ${catBadgesHTML}
        </div>
        ${this._renderOptionSettings(opt.settings)}
        ${mediaHTML}
        <div class="sd-option-fields">
          <div class="sd-option-field">
            <div class="sd-option-field-label">Setup</div>
            <div class="sd-option-field-text">${this._esc(opt.setup || "—")}</div>
          </div>
          <div class="sd-option-field">
            <div class="sd-option-field-label">Reads</div>
            <div class="sd-option-field-text">${this._esc(opt.reads || "—")}</div>
          </div>
        </div>`;
    }

    /* ══════════════════════════════════════════════
       OPTION MODAL (used by Playcall Sheet)
       ══════════════════════════════════════════════ */

    _openOptionModal(fi, pi, oi) {
      const formation = this.data.formations[fi];
      if (!formation) return;
      const play = (formation.plays || [])[pi];
      if (!play) return;
      const opt = (play.options || [])[oi];
      if (!opt) return;

      /* Remove existing modal if any */
      const existing = this.querySelector(".sd-modal-overlay");
      if (existing) existing.remove();

      /* Build media */
      const hasImage = opt.image && opt.image.url;
      const hasVideo = opt.video && opt.video.url;
      let mediaHTML = "";

      if (hasImage || hasVideo) {
        const imageBlock = hasImage
          ? `<div class="sd-option-media sd-option-media--image"><img src="${this._esc(opt.image.url)}" alt="${this._esc(opt.title || "")}" loading="lazy"></div>`
          : "";
        const videoEmbedClass = hasVideo && this._isVimeo(opt.video.url) ? " sd-option-media--embed" : "";
        const videoBlock = hasVideo
          ? `<div class="sd-option-media sd-option-media--video${videoEmbedClass}">${this._videoInnerHTML(opt.video.url)}</div>`
          : "";
        if (hasImage && hasVideo) {
          mediaHTML = `<div class="sd-option-media-grid">${imageBlock}${videoBlock}</div>`;
        } else {
          mediaHTML = imageBlock || videoBlock;
        }
      }

      /* Build category badges */
      const optCats = opt.categories || [];
      const catBadgesHTML = optCats.length
        ? `<div class="sd-option-categories">${optCats.map((c) => `<span class="sd-play-category">${this._esc(c)}</span>`).join("")}</div>`
        : "";

      /* Build modal */
      const modalOptFavId = this._optionFavId(play.id, oi);
      const isModalOptFav = this.favorites.includes(modalOptFavId);
      const overlay = document.createElement("div");
      overlay.className = "sd-modal-overlay";
      overlay.innerHTML = `
        <div class="sd-modal">
          <div class="sd-modal-header">
            <div class="sd-modal-header-left">
              <span class="sd-modal-formation">${this._esc(formation.name)}</span>
              <span class="sd-modal-play-name">${this._esc(play.name)}</span>
            </div>
            <div class="sd-modal-header-right">
              <button class="sd-fav-btn${isModalOptFav ? " favorited" : ""}" data-fav-id="${this._esc(modalOptFavId)}" title="Toggle favorite">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
              </button>
              <button class="sd-modal-close" aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
          </div>
          <div class="sd-modal-body">
            <div class="sd-option-title-row">
              <h4 class="sd-option-title">${this._esc(opt.title || `Option ${+oi + 1}`)}</h4>
              ${catBadgesHTML}
            </div>
            ${mediaHTML}
            <div class="sd-option-fields">
              <div class="sd-option-field">
                <div class="sd-option-field-label">Setup</div>
                <div class="sd-option-field-text">${this._esc(opt.setup || "—")}</div>
              </div>
              <div class="sd-option-field">
                <div class="sd-option-field-label">Reads</div>
                <div class="sd-option-field-text">${this._esc(opt.reads || "—")}</div>
              </div>
            </div>
          </div>
        </div>
      `;

      this.appendChild(overlay);

      /* Prevent body scroll */
      document.body.style.overflow = "hidden";

      /* Animate in */
      requestAnimationFrame(() => overlay.classList.add("open"));

      /* Close handlers */
      const close = () => {
        overlay.classList.remove("open");
        document.body.style.overflow = "";
        /* Pause any playing video */
        const vid = overlay.querySelector("video");
        if (vid) vid.pause();
        setTimeout(() => overlay.remove(), 250);
      };

      overlay.querySelector(".sd-modal-close").addEventListener("click", close);
      overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
      });

      /* Bind fav button in modal */
      const modalFavBtn = overlay.querySelector(".sd-fav-btn");
      if (modalFavBtn) {
        modalFavBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this._toggleFavorite(modalFavBtn.dataset.favId, modalFavBtn);
        });
      }

      /* Bind lightbox on modal images */
      overlay.querySelectorAll(".sd-option-media--image").forEach((media) => {
        media.addEventListener("click", (e) => {
          e.stopPropagation();
          const img = media.querySelector("img");
          if (img) this._openImageLightbox(img.currentSrc || img.src, img.alt);
        });
      });

      /* Escape key */
      const escHandler = (e) => {
        if (e.key === "Escape") {
          close();
          document.removeEventListener("keydown", escHandler);
        }
      };
      document.addEventListener("keydown", escHandler);
    }

    /* Navigate to Formations tab, open formation + play + optional option tab */
    _jumpToPlay(fi, pi, optionIndex) {
      /* Switch to formations tab */
      const formTab = this.querySelector('[data-tab="formations"]');
      if (formTab) formTab.click();

      /* Open the formation */
      const formation = this.querySelector(`[data-formation-index="${fi}"]`);
      if (formation && !formation.classList.contains("open")) {
        formation.classList.add("open");
        const hdr = formation.querySelector(".sd-formation-header");
        if (hdr) hdr.setAttribute("aria-expanded", "true");
      }

      /* Open the play */
      if (formation) {
        const play = formation.querySelector(`[data-play-index="${pi}"]`);
        if (play) {
          if (!play.classList.contains("open")) {
            play.classList.add("open");
          }

          /* Activate the correct option if specified */
          if (optionIndex != null) {
            const setter = play.__sdOptionNavSet;
            if (typeof setter === "function") setter(parseInt(optionIndex, 10));
          }

          /* Scroll into view */
          setTimeout(() => {
            play.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }
      }
    }

    /* Intro nav prev/next slider (formations with multiple intros) */
    _bindIntroNav(nav, panels) {
      if (!nav || !panels || !panels.length) return;
      const total = parseInt(nav.dataset.total, 10);
      let current = 0;
      const label = nav.querySelector("[data-sd-nav-label]");
      const prevBtn = nav.querySelector("[data-sd-nav-prev]");
      const nextBtn = nav.querySelector("[data-sd-nav-next]");
      const names = [];
      panels.forEach((p, i) => {
        const title = p.querySelector(".sd-formation-intro-title");
        names.push(title ? title.textContent : `Intro ${i + 1}`);
      });

      const update = () => {
        panels.forEach((p, i) => p.classList.toggle("active", i === current));
        if (label) label.innerHTML = `<span class="sd-option-nav-name">${this._esc(names[current])}</span><span class="sd-option-nav-count">${current + 1} / ${total}</span>`;
        prevBtn.classList.toggle("disabled", current === 0);
        nextBtn.classList.toggle("disabled", current === total - 1);
        nav.dataset.current = String(current);
      };

      prevBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (current > 0) { current--; update(); }
      });
      nextBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (current < total - 1) { current++; update(); }
      });

      update();
    }

    /* Option nav prev/next slider */
    _bindOptionNav(nav, panels) {
      if (!nav || !panels || !panels.length) return;
      const total = parseInt(nav.dataset.total, 10);
      let current = 0;
      const label = nav.querySelector("[data-sd-nav-label]");
      const prevBtn = nav.querySelector("[data-sd-nav-prev]");
      const nextBtn = nav.querySelector("[data-sd-nav-next]");
      const names = [];
      panels.forEach((p, i) => {
        const title = p.querySelector(".sd-option-title");
        names.push(title ? title.textContent : `Option ${i + 1}`);
      });

      const update = () => {
        panels.forEach((p, i) => p.classList.toggle("active", i === current));
        if (label) label.innerHTML = `<span class="sd-option-nav-name">${this._esc(names[current])}</span><span class="sd-option-nav-count">${current + 1} / ${total}</span>`;
        prevBtn.classList.toggle("disabled", current === 0);
        nextBtn.classList.toggle("disabled", current === total - 1);
        nav.dataset.current = String(current);
      };

      prevBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (current > 0) { current--; update(); }
      });
      nextBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (current < total - 1) { current++; update(); }
      });

      /* Expose setter on the parent .sd-play for playcall-sheet deep-linking */
      const play = nav.closest(".sd-play");
      if (play) {
        play.__sdOptionNavSet = (idx) => {
          if (typeof idx !== "number" || isNaN(idx)) return;
          current = Math.max(0, Math.min(total - 1, idx));
          update();
        };
      }

      update();
    }

    /* Escape HTML */
    _esc(str) {
      const d = document.createElement("div");
      d.textContent = str || "";
      return d.innerHTML;
    }

    /* Returns a Vimeo player embed URL (with hash for unlisted videos)
       or null if the URL isn't a Vimeo link. */
    _parseVimeoEmbed(url) {
      if (!url) return null;
      const m = url.match(/(?:vimeo\.com\/(?:video\/|manage\/videos\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?|player\.vimeo\.com\/video\/)(\d+)(?:[\/?#]?h=([a-zA-Z0-9]+)|\/([a-zA-Z0-9]+))?/);
      if (!m) return null;
      const id = m[1];
      const hash = m[2] || m[3];
      return hash
        ? `https://player.vimeo.com/video/${id}?h=${hash}`
        : `https://player.vimeo.com/video/${id}`;
    }

    /* Returns the inner element for a video: Vimeo iframe or native <video>.
       Caller wraps it in their own container. */
    _videoInnerHTML(url) {
      const embed = this._parseVimeoEmbed(url);
      if (embed) {
        return `<iframe src="${embed}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe>`;
      }
      return `<video src="${this._esc(url)}" controls playsinline></video>`;
    }

    _isVimeo(url) {
      return !!this._parseVimeoEmbed(url);
    }

    /* ══════════════════════════════════════════════
       SHOPIFY EDITOR RESET
       ══════════════════════════════════════════════ */

    reset() {
      this.activeLevel = this._levelFromURL();
      this.favorites = this._loadFavorites();
      this.finderFilter = "all";
      this.finderQuery = "";
      this.bindTabs();
      this.renderIntro();
      this.renderMacros();
      this.loadData();
      this.bindLevelToggle();
      this.bindHeroImageLightbox();
      this.bindOptionImageLightbox();
    }
  }

  customElements.define("scheme-detail-component", SchemeDetailComponent);
}

/* ── Shopify editor: re-initialize on section re-render ── */
if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", (e) => {
    const section = e.target;
    const component = section.querySelector("scheme-detail-component");
    if (component) component.reset();
  });
}
